import {SHOT_TEXTURE_KEYS} from '../../configs/shotAnimation';

/**
 * Текстуры снарядов выстрела: валун катапульты, стрела эльфов и искра
 * попадания. Картинок для них в public нет — пиксели считаются кодом и
 * регистрируются через textures.addUint8Array.
 *
 * ПОЧЕМУ НЕ CANVAS: canvas-текстура после потери GPU-контекста (мобильные в
 * фоне) остается пустой, а текстура из массива восстанавливается из памяти
 * (подробно — utils/sprites/generateOutlineTexture).
 *
 * ФОРМАТ: RGBA построчно сверху вниз, цвет НЕ умножен на альфу. На альфу его
 * умножает сам браузер при загрузке в видеокарту: Phaser перед texImage2D
 * ставит UNPACK_PREMULTIPLY_ALPHA_WEBGL (pma у текстуры по умолчанию true), и
 * флаг действует и на массивы. Умножить заранее — значит умножить дважды:
 * полупрозрачные пиксели темнеют, и мягкое свечение искры на светлом фоне
 * превращается в серое кольцо.
 *
 * Размеры — степени двойки: у таких текстур нет ограничений WebGL1 на
 * фильтрацию и повтор. Сглаживание при повороте дает LINEAR (см.
 * ensureShotTextures); мип-уровни при нем не используются.
 */

export const SHOT_TEXTURE_SIZES = {
  [SHOT_TEXTURE_KEYS.BOULDER]: {width: 64, height: 64},
  // Стрела смотрит ВПРАВО (угол 0): спрайт поворачивают по касательной к дуге
  [SHOT_TEXTURE_KEYS.ARROW]: {width: 128, height: 32},
  [SHOT_TEXTURE_KEYS.SPARK]: {width: 64, height: 64},
};

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const clampByte = (value) => Math.min(255, Math.max(0, Math.round(value)));

// Запись пикселя: цвет 0..255 как есть (на альфу его умножит браузер при
// загрузке текстуры), альфа 0..1
const putPixel = (pixels, width, x, y, r, g, b, alpha) => {
  const offset = (y * width + x) * 4;
  pixels[offset] = clampByte(r);
  pixels[offset + 1] = clampByte(g);
  pixels[offset + 2] = clampByte(b);
  pixels[offset + 3] = clampByte(255 * clamp01(alpha));
};

// Детерминированная «зернистость» камня: одна и та же у всех клиентов и во
// всех запусках, без Math.random
const grain = (x, y) => {
  const value = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

/**
 * Валун: шар с боковым светом, темной кромкой и зерном камня. Кромка нужна,
 * чтобы серый камень читался и на светлой подложке, и на темной.
 */
export const buildBoulderPixels = () => {
  const {width, height} = SHOT_TEXTURE_SIZES[SHOT_TEXTURE_KEYS.BOULDER];
  const pixels = new Uint8Array(width * height * 4);
  const centerX = (width - 1) / 2;
  const centerY = (height - 1) / 2;
  const radius = width / 2 - 2;
  // Свет слева сверху, чуть на зрителя
  const lightLength = Math.hypot(-0.5, -0.62, 0.6);
  const light = {x: -0.5 / lightLength, y: -0.62 / lightLength, z: 0.6 / lightLength};

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const dx = (x - centerX) / radius;
      const dy = (y - centerY) / radius;
      const distance = Math.hypot(dx, dy);
      // Сглаженный край: альфа спадает на последнем пикселе радиуса
      const alpha = clamp01((1 - distance) * radius + 0.5);
      if (alpha <= 0) {
        continue;
      }

      const normalZ = Math.sqrt(Math.max(0, 1 - Math.min(1, distance * distance)));
      const lit = Math.max(0, dx * light.x + dy * light.y + normalZ * light.z);
      // Темная кромка в последних ~12% радиуса
      const rim = clamp01((distance - 0.86) / 0.14);
      const shade = (0.5 + 0.62 * lit) * (1 - 0.62 * rim) * (0.9 + 0.2 * grain(x, y));

      // Светлый камень: темно-серый терялся на темной подложке поля
      putPixel(
        pixels, width, x, y,
        Math.min(255, 212 * shade), Math.min(255, 202 * shade), Math.min(255, 184 * shade), alpha
      );
    }
  }

  return pixels;
};

// Расстояние от точки до отрезка
const distanceToSegment = (px, py, ax, ay, bx, by) => {
  const abX = bx - ax;
  const abY = by - ay;
  const t = clamp01(((px - ax) * abX + (py - ay) * abY) / (abX * abX + abY * abY));
  return Math.hypot(px - (ax + abX * t), py - (ay + abY * t));
};

// Внутри ли точка треугольника (знаки трех векторных произведений)
const checkIfInsideTriangle = (px, py, [ax, ay], [bx, by], [cx, cy]) => {
  const cross = (x1, y1, x2, y2, x3, y3) => (x1 - x3) * (y2 - y3) - (x2 - x3) * (y1 - y3);
  const d1 = cross(px, py, ax, ay, bx, by);
  const d2 = cross(px, py, bx, by, cx, cy);
  const d3 = cross(px, py, cx, cy, ax, ay);
  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNegative && hasPositive);
};

/**
 * Стрела, смотрит вправо: светлое древко, стальной наконечник, белое оперение
 * и темная кромка вокруг — стрела маленькая и должна читаться на любой
 * подложке. Хвост древка полупрозрачный: на лету стрела оставляет за собой
 * короткий след.
 */
export const buildArrowPixels = () => {
  const {width, height} = SHOT_TEXTURE_SIZES[SHOT_TEXTURE_KEYS.ARROW];
  const pixels = new Uint8Array(width * height * 4);
  const midY = height / 2;

  // Фигуры крупные: на поле стрела длиной с ячейку, тонкое древко терялось
  const shaft = {fromX: 14, toX: 94, halfWidth: 3.6};
  // Острие не доходит до края текстуры на толщину кромки: иначе кромка
  // обрезалась бы краем
  const head = [[122, midY], [88, midY - 12], [88, midY + 12]];
  const feathers = [
    [[4, midY - 13], [34, midY - 2], [14, midY - 2]],
    [[4, midY + 13], [34, midY + 2], [14, midY + 2]],
  ];
  const outlineWidth = 2.2;

  // Цвет и непрозрачность фигуры в точке (без сглаживания)
  const sample = (px, py) => {
    if (checkIfInsideTriangle(px, py, ...head)) {
      return {r: 236, g: 240, b: 244, a: 1};
    }
    const shaftDistance = distanceToSegment(px, py, shaft.fromX, midY, shaft.toX, midY);
    if (shaftDistance <= shaft.halfWidth) {
      // След: хвост древка тает
      const along = clamp01((px - shaft.fromX) / (shaft.toX - shaft.fromX));
      return {r: 255, g: 240, b: 196, a: 0.6 + 0.4 * along};
    }
    if (feathers.some((feather) => checkIfInsideTriangle(px, py, ...feather))) {
      return {r: 255, g: 255, b: 255, a: 0.95};
    }

    // Темная кромка вокруг древка и наконечника
    const headDistance = Math.min(
      distanceToSegment(px, py, ...head[0], ...head[1]),
      distanceToSegment(px, py, ...head[1], ...head[2]),
      distanceToSegment(px, py, ...head[2], ...head[0])
    );
    if (shaftDistance <= shaft.halfWidth + outlineWidth || headDistance <= outlineWidth) {
      return {r: 34, g: 24, b: 14, a: 0.78};
    }
    return null;
  };

  // Сглаживание: 3×3 отсчета на пиксель
  const SUBSAMPLES = 3;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let sy = 0; sy < SUBSAMPLES; sy += 1) {
        for (let sx = 0; sx < SUBSAMPLES; sx += 1) {
          const hit = sample(x + (sx + 0.5) / SUBSAMPLES, y + (sy + 0.5) / SUBSAMPLES);
          if (hit) {
            r += hit.r * hit.a;
            g += hit.g * hit.a;
            b += hit.b * hit.a;
            a += hit.a;
          }
        }
      }
      if (a > 0) {
        // Средний цвет покрытых отсчетов, альфа — доля покрытия
        putPixel(pixels, width, x, y, r / a, g / a, b / a, a / (SUBSAMPLES * SUBSAMPLES));
      }
    }
  }

  return pixels;
};

/** Искра попадания: мягкое светлое пятно, яркое в центре */
export const buildSparkPixels = () => {
  const {width, height} = SHOT_TEXTURE_SIZES[SHOT_TEXTURE_KEYS.SPARK];
  const pixels = new Uint8Array(width * height * 4);
  const centerX = (width - 1) / 2;
  const centerY = (height - 1) / 2;
  const radius = width / 2;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const distance = Math.hypot(x - centerX, y - centerY) / radius;
      if (distance >= 1) {
        continue;
      }
      const falloff = (1 - distance) * (1 - distance);
      // Теплый белый: в центре почти белый, к краю желтее
      putPixel(pixels, width, x, y, 255, 250 - 40 * distance, 225 - 110 * distance, falloff);
    }
  }

  return pixels;
};

const PIXEL_BUILDERS = {
  [SHOT_TEXTURE_KEYS.BOULDER]: buildBoulderPixels,
  [SHOT_TEXTURE_KEYS.ARROW]: buildArrowPixels,
  [SHOT_TEXTURE_KEYS.SPARK]: buildSparkPixels,
};

/**
 * Зарегистрировать текстуры снарядов в сцене (один раз: готовые не трогает).
 *
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR: у текстуры
 *   из массива по умолчанию NEAREST, и повернутая стрела пошла бы ступеньками
 * @returns {boolean} все ли текстуры на месте; false — анимации не будет
 */
export const ensureShotTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }

  return Object.values(SHOT_TEXTURE_KEYS).every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const {width, height} = SHOT_TEXTURE_SIZES[key];
    const texture = textures.addUint8Array(key, PIXEL_BUILDERS[key](), width, height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
